import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Axios from "axios";
import Sidebar from "../../components/Sidebar";
import Topbar from "../../components/Topbar";
import { Icon } from "@iconify/react";
import JobCalendarPageStandalone from "./JobCalendarPageStandalone";
import JobListPage from "./JobListPage";

export default function JobManagementPage() {
  const location = useLocation();
  const hasListQuery = (() => {
    const params = new URLSearchParams(location.search);
    return (
      params.has("search") ||
      params.has("page") ||
      params.has("pageSize") ||
      params.has("focus")
    );
  })();
  const initialTab = location.state?.activeTab === "list" || hasListQuery ? "list" : "calendar";
  const [activeTab, setActiveTab] = useState(initialTab); // "calendar", "list", or null
  const [branches, setBranches] = useState([]);
  const [branchFilter, setBranchFilter] = useState("all");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const hasQuery =
      params.has("search") ||
      params.has("page") ||
      params.has("pageSize") ||
      params.has("focus");
    if (location.state?.activeTab === "list" || hasQuery) {
      setActiveTab("list");
    }
  }, [location.state?.activeTab, location.search]);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const response = await Axios.get(
          `${process.env.REACT_APP_API_URL}/api/app/org/branches`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
            },
          }
        );
        if (response.data?.status === "OK" && Array.isArray(response.data?.results)) {
          setBranches(response.data.results);
        } else {
          setBranches([]);
        }
      } catch {
        setBranches([]);
      }
    };
    fetchBranches();
  }, []);

  return (
    <div className="flex">
      <Sidebar />

      <div
        className="flex-1 h-screen bg-gray-50 flex flex-col"
        style={{
          height: "100vh",
          overflowX: "hidden",
          overflowY: "hidden",
          marginLeft: "var(--sidebar-width)",
        }}
      >
        <Topbar
          breadcrumbs={[
            { label: "งานช่าง", link: "/job-management" },
            { label: "จัดการจ๊อบงาน" },
          ]}
        />

        <div className="pl-2 pr-8 pt-16 pb-0 shrink-0 overflow-hidden">
          {/* ACTION CARDS */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">

            {/* Card: ปฏิทินงานช่าง */}
            <button
              onClick={() => setActiveTab("calendar")}
              className={`h-[68px] px-4 py-1 rounded-xl shadow transition cursor-pointer text-left border-2 ${
                activeTab === "calendar" 
                  ? "ring-2 ring-[#E13F57] ring-offset-2 border-[#E13F57] bg-red-50" 
                  : "bg-white border-transparent hover:shadow-lg"
              }`}
            >
              <div className="flex items-center gap-2">
                <div className={`p-2 rounded-full ${
                  activeTab === "calendar" ? "bg-[#E13F57]" : "bg-blue-100"
                }`}>
                  <Icon 
                    icon="mdi:calendar-month" 
                    className={activeTab === "calendar" ? "text-white" : "text-blue-700"} 
                    width={32} 
                  />
                </div>

                <div>
                  <h2 className={`text-lg font-semibold ${
                    activeTab === "calendar" ? "text-[#E13F57]" : "text-gray-900"
                  }`}>
                    ปฏิทินงานช่าง
                  </h2>
                  <p className="text-gray-600 text-sm mt-0.5">
                    ดูตารางงานช่าง ปรับวันงานด้วยการลาก–วาง และจัดทีมได้
                  </p>
                </div>
              </div>
            </button>

            {/* Card: ตารางงานทั้งหมด */}
            <div
              className={`h-[68px] px-4 py-1 rounded-xl shadow transition cursor-pointer text-left border-2 ${
                activeTab === "list" 
                  ? "ring-2 ring-[#E13F57] ring-offset-2 border-[#E13F57] bg-red-50" 
                  : "bg-white border-transparent hover:shadow-lg"
              }`}
            >
              <div className="flex items-center justify-between gap-3 h-full">
                <button
                  type="button"
                  onClick={() => setActiveTab("list")}
                  className="flex items-center gap-2 flex-1 text-left bg-transparent border-0 p-0"
                >
                  <div className={`p-2 rounded-full ${
                    activeTab === "list" ? "bg-[#E13F57]" : "bg-green-100"
                  }`}>
                    <Icon 
                      icon="mdi:table" 
                      className={activeTab === "list" ? "text-white" : "text-green-700"} 
                      width={32} 
                    />
                  </div>

                  <div>
                    <h2 className={`text-lg font-semibold ${
                      activeTab === "list" ? "text-[#E13F57]" : "text-gray-900"
                    }`}>
                      ตารางงานทั้งหมด
                    </h2>
                    <p className="text-gray-600 text-sm mt-0.5">
                      ค้นหา แก้ไข และดูข้อมูลจ๊อบงานในรูปแบบรายการ
                    </p>
                  </div>
                </button>
                {activeTab === "calendar" && (
                  <select
                    value={branchFilter}
                    onChange={(e) => setBranchFilter(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className="h-[36px] min-w-[220px] border border-gray-300 rounded-md px-3 py-1 text-sm bg-white"
                  >
                    <option value="all">สาขา: ทั้งหมด</option>
                    {branches.map((b) => (
                      <option key={String(b.Branch_Id)} value={String(b.Branch_Id)}>
                        {b.Branch_Name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>

          </div>
        </div>

        {/* Tab Content */}
        {activeTab === "calendar" && (
          <div className="flex-1 min-h-0 pb-0 overflow-hidden">
            <JobCalendarPageStandalone branchFilter={branchFilter} />
          </div>
        )}
        {activeTab === "list" && (
          <div className="flex-1 min-h-0 pb-0">
            <JobListPage
              contentOnly={true}
              branchFilter={branchFilter}
              onBranchFilterChange={setBranchFilter}
            />
          </div>
        )}
      </div>
    </div>
  );
}
